import axios, {
    AxiosError,
    AxiosHeaders,
    InternalAxiosRequestConfig,
} from 'axios';
import { User as FirebaseUser } from 'firebase/auth';
import { API_BASE_URL, APP, CLIENT_VERSION } from '../constants';
import { firebaseAuth } from '../firebase';

interface RetryRequestConfig extends InternalAxiosRequestConfig {
    _is401Retry?: boolean;
}

// sent by the backend when this bundle is too old for it
const STALE_FRONTEND_STATUS = 426;

class ApiClient {
    user: FirebaseUser | null;
    axiosInstance;
    isReloadingStaleFrontend = false;
    // superUserKey: string | null;

    constructor() {
        this.user = null;
        // this.superUserKey = null;
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                App: APP,
                // tells the backend the token comes from this brand's own Firebase project
                'X-Auth-Version': '2',
                'X-Client-Version': String(CLIENT_VERSION),
            },
        });
        this.handle401 = this.handle401.bind(this);

        this.axiosInstance.interceptors.request.use((config) => {
            config.params = {
                ...config.params,
                // ...(this.superUserKey && { superUserKey: this.superUserKey }),
            };
            return config;
        });

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            this.handle401
        );
    }

    async init(firebaseUser: FirebaseUser) {
        const accessToken = await firebaseUser.getIdToken();
        this.setAccessToken(accessToken);
        this.user = firebaseUser;
    }

    // setSuperUserKey(key: string | null) {
    //     this.superUserKey = key;
    // }

    setAccessToken(token?: string) {
        this.axiosInstance.defaults.headers.common['Authorization'] =
            token || '';
    }

    signOut() {
        this.user = null;
        // this.superUserKey = null;
        this.axiosInstance.defaults.headers.common['Authorization'] = '';
    }

    async reloadStaleFrontend() {
        if (this.isReloadingStaleFrontend) return;
        this.isReloadingStaleFrontend = true;

        try {
            await firebaseAuth.signOut();
        } finally {
            window.location.reload();
        }
    }

    // refresh access token in case of 401 api error
    async handle401(e: AxiosError) {
        if (e.response?.status === STALE_FRONTEND_STATUS) {
            this.reloadStaleFrontend();
            return Promise.reject(e);
        }

        const config = e.config as RetryRequestConfig | undefined;
        if (e.response?.status !== 401 || !config || config._is401Retry) {
            // Sentry.captureException(e);
            return Promise.reject(e);
        }

        const user = this.user;
        if (!user) return Promise.reject(e);

        try {
            const newToken = await user.getIdToken(true);
            if (this.user !== user) return Promise.reject(e);

            this.axiosInstance.defaults.headers.common['Authorization'] =
                newToken;
            config.headers = AxiosHeaders.from(config.headers);
            config.headers.set('Authorization', newToken);
            config._is401Retry = true;
            return this.axiosInstance(config);
        } catch (_e) {
            return Promise.reject(_e);
        }
    }
}

const ApiClientInstance = new ApiClient();

export const api = ApiClientInstance.axiosInstance;

export default ApiClientInstance;
